import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { Button, Drawer, Tag, message } from "antd";
import {
  CheckCircleOutlined,
  CloseOutlined,
  CopyOutlined,
  DownOutlined,
  LeftOutlined,
  MailOutlined,
  RightOutlined,
  UpOutlined,
} from "@ant-design/icons";
import dayjs from "dayjs";
import { QUESTION_TYPE } from "pages/SurveyManagement/constants";
import {
  channelLabelKey,
  FAST_COMPLETION_SECONDS,
  consentSummary,
  durationSeconds,
  formatAnswerValue,
  formatDuration,
  isCallResponse,
  respondentEmail,
  respondentInitials,
  respondentName,
  responseAsText,
  responseMetaLines,
  responseNumber,
} from "./responseFormat";
import CallConversation from "./CallConversation";
import styles from "./style.module.scss";

const CHOICE_TYPES = [QUESTION_TYPE.SINGLE_CHOICE, QUESTION_TYPE.MULTIPLE_CHOICE, QUESTION_TYPE.RATING];

/**
 * One response at a time: who answered, every answer in survey order, and ‹ › to move through the
 * rows the table currently shows. Technical details stay folded under "More info".
 */
// Recorded answers carry no duration in their header, so a player shows a black 0:00 until it
// plays; "#t=0.1" makes it show the first frame. The fragment never reaches the server, so the
// signed URL stays valid.
export const firstFrameUrl = (url) => (url && !url.includes("#") ? `${url}#t=0.1` : url);

// A video answer: the player, its length and the transcript when the survey asks for one.
const VideoAnswerView = ({ video, label }) => {
  const { t } = useTranslation();
  if (video.deleted_at) {
    return <div className={styles.qaAnswer}>{t("feedbackCapture.responsesPage.video_deleted")}</div>;
  }
  return (
    <div className={styles.videoAnswer}>
      {video.url ? (
        <video className={styles.videoPlayer} src={firstFrameUrl(video.url)} controls playsInline preload="metadata" />
      ) : null}
      <div className={styles.videoMeta}>
        {label}
        {video.url ? (
          <>
            {" · "}
            <a href={firstFrameUrl(video.url)} target="_blank" rel="noopener noreferrer">
              {t("feedbackCapture.responsesPage.video_open")}
            </a>
          </>
        ) : null}
      </div>
      {video.transcript ? (
        <div className={styles.videoTranscript}>
          <div className={styles.videoTranscriptLabel}>{t("feedbackCapture.responsesPage.video_transcript")}</div>
          {video.transcript}
        </div>
      ) : null}
      {!video.transcript && video.transcript_status === "failed" ? (
        <div className={styles.videoMeta}>{t("feedbackCapture.responsesPage.video_transcript_failed")}</div>
      ) : null}
    </div>
  );
};

const ResponseDetailDrawer = ({
  open,
  response,
  position,
  total,
  allResponses,
  questions,
  surveyTitle,
  instrumentId,
  onPrev,
  onNext,
  onClose,
}) => {
  const { t } = useTranslation();
  const [moreOpen, setMoreOpen] = useState(false);

  useEffect(() => {
    setMoreOpen(false);
  }, [response?.id]);

  if (!response) return null;

  const name = respondentName(response);
  const email = respondentEmail(response);
  const number = responseNumber(response, allResponses);
  const heading = name || email || t("feedbackCapture.responsesPage.response_number", { number });
  const duration = durationSeconds(response);
  const durationLabel = formatDuration(duration, t);
  const isFast = duration != null && duration < FAST_COMPLETION_SECONDS;
  const metaLines = responseMetaLines(response, t);
  const consentLine = consentSummary(response, t);
  const typeById = Object.fromEntries((questions || []).map((q) => [q.id, q.type]));
  const order = (questions || []).map((q) => q.id);
  const answers = [...(response.answers || [])].sort((a, b) => {
    const ia = order.indexOf(a.question_id);
    const ib = order.indexOf(b.question_id);
    return (ia < 0 ? 999 : ia) - (ib < 0 ? 999 : ib);
  });

  const copyResponse = async () => {
    try {
      await navigator.clipboard.writeText(responseAsText(response, questions, t));
      message.success(t("feedbackCapture.responsesPage.copied"));
    } catch (_) {
      message.error(t("feedbackCapture.responsesPage.copy_failed"));
    }
  };

  const mailto = email
    ? `mailto:${encodeURIComponent(email)}?subject=${encodeURIComponent(
        t("feedbackCapture.responsesPage.reply_subject", { title: surveyTitle || "" })
      )}`
    : null;

  return (
    <Drawer
      open={open}
      onClose={onClose}
      width={600}
      closable={false}
      destroyOnClose
      className={styles.detailDrawer}
      title={null}
    >
      <div className={styles.detailHead}>
        <div className={styles.detailNav}>
          <button type="button" className={styles.linkBtn} onClick={onClose}>
            <CloseOutlined /> {t("feedbackCapture.responsesPage.close")}
          </button>
          <div className={styles.pager}>
            <button
              type="button"
              className={styles.pagerBtn}
              onClick={onPrev}
              disabled={position <= 1}
              aria-label={t("feedbackCapture.responsesPage.previous")}
            >
              <LeftOutlined />
            </button>
            <span>
              {position} / {total}
            </span>
            <button
              type="button"
              className={styles.pagerBtn}
              onClick={onNext}
              disabled={position >= total}
              aria-label={t("feedbackCapture.responsesPage.next")}
            >
              <RightOutlined />
            </button>
          </div>
        </div>
        <div className={styles.who}>
          <div className={styles.avatar}>{respondentInitials(response) || `#${number}`}</div>
          <div>
            <h2 className={styles.whoName}>{heading}</h2>
            {email && name ? <p className={styles.whoEmail}>{email}</p> : null}
          </div>
        </div>
        <div className={styles.detailTags}>
          {response.status !== "completed" ? (
            <Tag>{t("feedbackCapture.response_status_empty")}</Tag>
          ) : null}
          {response.channel_type ? (
            <Tag>{t(channelLabelKey(response))}</Tag>
          ) : null}
          {response.company_domain ? <Tag>{response.company_domain}</Tag> : null}
        </div>
        <div className={styles.detailActions}>
          {mailto ? (
            <Button type="primary" icon={<MailOutlined />} href={mailto}>
              {t("feedbackCapture.responsesPage.reply_by_email")}
            </Button>
          ) : null}
          <Button icon={<CopyOutlined />} onClick={copyResponse}>
            {t("feedbackCapture.responsesPage.copy")}
          </Button>
        </div>
        {response.prior_response_count > 0 ? (
          <div className={styles.priorNote}>
            {t("feedbackCapture.response_prior_count", { count: response.prior_response_count })}
          </div>
        ) : null}
      </div>

      <div className={styles.detailBody}>
        {answers.map((answer) => {
          const value = answer.display_value || formatAnswerValue(answer.value) || "—";
          const isChoice = CHOICE_TYPES.includes(typeById[answer.question_id] || answer.type);
          if (answer.video) {
            return (
              <div key={answer.question_id} className={styles.qa}>
                <div className={styles.qaQuestion}>{answer.question_text || answer.question_id}</div>
                <VideoAnswerView video={answer.video} label={value} />
              </div>
            );
          }
          return (
            <div key={answer.question_id} className={styles.qa}>
              <div className={styles.qaQuestion}>{answer.question_text || answer.question_id}</div>
              <div className={styles.qaAnswer}>
                {isChoice ? (
                  <span className={styles.pill}>{value}</span>
                ) : (typeById[answer.question_id] || answer.type) === QUESTION_TYPE.URL &&
                  /^https?:\/\//i.test(String(answer.value || "")) ? (
                  <a href={answer.value} target="_blank" rel="noopener noreferrer">
                    {value}
                  </a>
                ) : (
                  value
                )}
              </div>
            </div>
          );
        })}
        {response.text ? (
          <div className={styles.qa}>
            <div className={styles.qaQuestion}>{t("feedbackCapture.response_text_capture")}</div>
            <div className={styles.qaAnswer}>{response.text}</div>
          </div>
        ) : null}
        {!answers.length && !response.text ? (
          <div className={styles.emptyBody}>{t("feedbackCapture.response_no_content")}</div>
        ) : null}
        {isCallResponse(response) && instrumentId ? (
          <CallConversation
            instrumentId={instrumentId}
            response={response}
            number={number}
            surveyTitle={surveyTitle}
          />
        ) : null}

        <div className={styles.metaBox}>
          {consentLine ? (
            <div className={styles.consentLine}>
              <CheckCircleOutlined /> {consentLine}
            </div>
          ) : null}
          <div className={styles.metaRow}>
            <span>
              {durationLabel ? (
                <span className={isFast ? styles.fast : undefined}>
                  {t("feedbackCapture.responsesPage.completion_time", { duration: durationLabel })}
                </span>
              ) : null}
              {durationLabel && response.completed_at ? " · " : null}
              {response.completed_at ? dayjs(response.completed_at).format("D MMM YYYY, HH:mm") : null}
            </span>
            {metaLines.length ? (
              <button type="button" className={styles.linkBtn} onClick={() => setMoreOpen((v) => !v)}>
                {t("feedbackCapture.responsesPage.more_info")} {moreOpen ? <UpOutlined /> : <DownOutlined />}
              </button>
            ) : null}
          </div>
          {moreOpen ? (
            <div className={styles.metaLines}>
              {metaLines.map((line) => (
                <div key={line}>{line}</div>
              ))}
            </div>
          ) : null}
        </div>
      </div>
    </Drawer>
  );
};

export default ResponseDetailDrawer;
